/**
 * THE INKWELL PAPERS
 * Copy is the approved manuscript, word-for-word.
 * Source: src/content/inkwell-papers/*.md — do not rewrite, shorten, or invent copy.
 */

import coverOne from "@/assets/papers-cover-one.jpg";
import portraitOne from "@/assets/papers-portrait-one.jpg";
import threshold from "@/assets/papers-threshold.jpg";
import essay from "@/assets/papers-essay.jpg";
import coverTwo from "@/assets/papers-cover-two.jpg";
import portraitTwo from "@/assets/papers-portrait-two.jpg";
import fog from "@/assets/papers-fog.jpg";
import closing from "@/assets/papers-closing.jpg";

export type Block =
  | { kind: "kicker"; text: string }
  | { kind: "heading"; text: string }
  | { kind: "subhead"; text: string }
  | { kind: "standfirst"; text: string }
  | { kind: "para"; text: string }
  | { kind: "pull"; text: string }
  | { kind: "quote"; text: string; attribution: string; note?: string }
  | { kind: "signoff"; text: string }
  | { kind: "rule" }
  | { kind: "image"; src: string; alt: string; width: number; height: number; frame?: boolean }
  | { kind: "list"; items: string[] }
  | { kind: "steps"; items: { label: string; text: string }[] }
  | { kind: "words"; items: string[] }
  | { kind: "colophon"; lines: string[] };

export type PaperPage = {
  n: number;
  label: string;
  tone: "dark" | "ivory" | "cover";
  hero?: { src: string; alt: string; width: number; height: number };
  blocks: Block[];
};

export type Paper = {
  id: string;
  issue: string;
  title: string;
  standfirst: string;
  date: string;
  cover: { src: string; alt: string; width: number; height: number };
  pages: PaperPage[];
};

const colophon = [
  "THE INKWELL",
  "A Marriage & Family Therapy Practice",
  "Berkeley, California • Telehealth across California",
  "theinkwelltherapy.com",
];

export const papers: Paper[] = [
  {
    id: "stories-we-carry",
    issue: "Issue One",
    title: "The Stories We Carry",
    standfirst: "Narrative, memory, identity & the possibility of revision",
    date: "July 2026",
    cover: { src: coverOne, alt: "Two figures walking a windswept shoreline under a vast clouded sky", width: 1280, height: 1600 },
    pages: [
      {
        n: 1,
        label: "Cover",
        tone: "cover",
        hero: { src: coverOne, alt: "Two figures walking a windswept shoreline under a vast clouded sky", width: 1280, height: 1600 },
        blocks: [
          { kind: "kicker", text: "The Inkwell Papers · Issue One · July 2026" },
          { kind: "heading", text: "The Stories We Carry" },
          { kind: "standfirst", text: "Narrative, memory, identity & the possibility of revision" },
          { kind: "colophon", lines: ["A journal from The Inkwell", "A Marriage & Family Therapy Practice"] },
        ],
      },
      {
        n: 2,
        label: "From The Inkwell",
        tone: "dark",
        blocks: [
          { kind: "kicker", text: "From The Inkwell" },
          { kind: "para", text: "We begin telling stories about ourselves long before we recognize them as stories." },
          { kind: "para", text: "I am the responsible one. I am difficult. I am the one who leaves. I am the one who stays. I am too much. I am not enough." },
          { kind: "para", text: "Families have stories. Communities have stories. Cultures have stories. So do relationships. Over time, repetition can make an interpretation feel like fact." },
          { kind: "para", text: "Therapy offers another possibility. Not the erasure of what happened. Not a prettier version of the past. Something more interesting than that: the opportunity to look again." },
          { kind: "para", text: "Who told the story? What was happening around you when you learned it? What evidence has been left out? What have you survived that the story fails to account for? And who might you become if one interpretation of your life no longer had to explain everything?" },
          { kind: "para", text: "Welcome to The Inkwell Papers." },
          { kind: "signoff", text: "— April" },
          {
            kind: "quote",
            text: "People are trapped in history and history is trapped in them.",
            attribution: "James Baldwin",
            note: "Editorial source note: James Baldwin, “Stranger in the Village,” first published in Harper’s Magazine (1953), later collected in Notes of a Native Son.",
          },
          { kind: "image", src: portraitOne, alt: "Profile portrait of a woman looking out toward the water at dusk", width: 1200, height: 1500 },
          { kind: "rule" },
          { kind: "heading", text: "The Stories We Carry" },
          { kind: "standfirst", text: "What narrative therapy understands about identity, memory, and change." },
          { kind: "para", text: "“I’ve always been this way.” It is one of those sentences that can sound like description when it is really a verdict. A person says it after another argument, another anxious night, another relationship that ended in a familiar place. The sentence gathers years of evidence around itself until it begins to feel less like a thought and more like a fact." },
          { kind: "para", text: "Narrative therapy starts with a different assumption: a person is larger than the story that has come to dominate the telling of their life. The point is not to deny patterns. Patterns matter. Consequences matter. Pain matters. But a pattern is not the whole person, and a conclusion is not the same thing as identity." },
          { kind: "para", text: "Human beings are meaning-making creatures. We organize experience into stories because stories help us understand what happened, who mattered, what was dangerous, what was expected, and what we should do next. A child who is repeatedly called difficult may learn to read disagreement as proof. A person praised almost entirely for achievement may eventually experience rest as failure. Someone who had to become self-sufficient too early may grow into an adult who experiences needing another person as weakness." },
          { kind: "para", text: "These stories rarely come from nowhere. They develop inside families, neighborhoods, schools, churches, relationships, histories and systems. Race, gender, class, migration, disability, religion, poverty, privilege, violence and belonging all affect which stories become available to us and which stories other people are willing to believe about us." },
          { kind: "pull", text: "A story can be true and still be incomplete." },
          { kind: "para", text: "This is one reason narrative therapy often separates the person from the problem. Instead of saying, “I am anxiety,” we can become curious about anxiety as something that enters, grows louder, retreats, persuades and sometimes loses influence. When does it arrive? What does it tell you? What does it ask you to avoid? When have you acted against its instructions? Those questions begin to uncover something the dominant story tends to hide: exceptions." },
          { kind: "para", text: "Exceptions matter because they are evidence. The person who believes they are weak may have survived conditions requiring extraordinary endurance. The person who believes they cannot speak up may remember one afternoon when they did. The person who calls themselves selfish may notice how many years they spent making themselves available to everyone else." },
          { kind: "para", text: "Narrative work is not positive thinking. It does not replace one rigid story with a flattering one. It asks for a fuller account. What else is true? Who witnessed another version of you? Which values were present even when the outcome was imperfect? Which parts of your life have been edited out because they did not fit the conclusion?" },
          { kind: "para", text: "A story can be true and still be incomplete." },
        ],
      },
      {
        n: 3,
        label: "Revision Is a Form of Hope",
        tone: "dark",
        hero: { src: threshold, alt: "A gate in a dune fence opening onto a path toward luminous water", width: 1400, height: 1000 },
        blocks: [
          { kind: "heading", text: "Revision Is a Form of Hope" },
          { kind: "standfirst", text: "You do not have to deny the past to change what it means." },
          {
            kind: "quote",
            text: "Narrative is radical, creating us at the very moment it is being created.",
            attribution: "Toni Morrison",
            note: "Editorial source note: Toni Morrison, Nobel Lecture, Stockholm, 1993.",
          },
          { kind: "para", text: "Revision can sound suspicious when the subject is a life. We associate revision with changing the record, softening what happened, or rewriting the past until it becomes easier to tolerate. Therapeutic revision asks for something different. It asks whether the meaning we assigned to an experience years ago is still the only meaning available to us now." },
          { kind: "para", text: "A child can decide, with complete sincerity, that a parent’s distance means: I am not worth staying for. An adult may later understand that the parent was ill, overwhelmed, addicted, frightened, immature, or trapped inside circumstances the child could not possibly have understood. The new information does not erase the abandonment. It changes the story around the wound." },
          { kind: "para", text: "Complexity is not betrayal. A person can say, “My mother loved me,” and “My mother hurt me,” without forcing one statement to cancel the other. A person can acknowledge that a relationship contained tenderness and still decide it was unsafe. They can understand why someone behaved as they did and still hold them accountable." },
          { kind: "para", text: "Many of our most stubborn adult patterns began as intelligent adaptations. Distrust may have protected you. Perfectionism may have earned safety or approval. Silence may have reduced conflict. Hyper-independence may have been the only reliable way to get through the day. Therapy becomes interesting when the question changes from, “What is wrong with me?” to, “What happened that made this response make sense?”" },
          { kind: "pull", text: "Revision is not denial. It is authorship." },
          { kind: "para", text: "Then comes the harder question: “What is this response costing me now?” A strategy can deserve gratitude and still be retired." },
          { kind: "para", text: "We can honor the younger self who learned it without requiring the adult self to live by it forever." },
          { kind: "para", text: "Revision also makes room for witnesses. Sometimes another person remembers us differently than we remember ourselves. A sibling remembers our courage. A teacher saw intelligence where the family story emphasized trouble. A friend remembers the season we describe as failure and recalls the generosity we offered everyone else while we were surviving it." },
          { kind: "para", text: "The goal is not a triumphant ending. It is authorship. It is the ability to say: this happened; it mattered; it shaped me; and it does not get to decide every sentence that comes next." },
          { kind: "para", text: "Revision is not denial. It is authorship." },
        ],
      },
      {
        n: 4,
        label: "What Remains",
        tone: "dark",
        blocks: [
          { kind: "kicker", text: "Photographic essay" },
          { kind: "heading", text: "What Remains" },
          { kind: "standfirst", text: "A photographic essay about memory, family, distance and return." },
          { kind: "image", src: essay, alt: "Footprints and dune grass on an empty beach", width: 1400, height: 1000, frame: true },
          { kind: "image", src: coverOne, alt: "Two figures walking the shoreline seen from a distance", width: 1280, height: 1600, frame: true },
          { kind: "image", src: threshold, alt: "A dune path at nightfall", width: 1400, height: 1000, frame: true },
          { kind: "rule" },
          { kind: "subhead", text: "Notice Before You Name" },
          { kind: "para", text: "Sit somewhere quiet." },
          { kind: "para", text: "For sixty seconds, resist explaining anything." },
          { kind: "para", text: "Notice what your body is doing. Where is there tension? What thought keeps returning? What emotion appears before you name it? What changes when you observe the experience without immediately turning it into a story?" },
          { kind: "para", text: "Sometimes the first revision is simply learning to notice what is happening before deciding what it means." },
        ],
      },
      {
        n: 5,
        label: "Write It Differently",
        tone: "ivory",
        blocks: [
          { kind: "heading", text: "Write It Differently" },
          { kind: "standfirst", text: "A narrative practice." },
          { kind: "subhead", text: "The Old Story" },
          { kind: "para", text: "Write one sentence you have repeated about yourself for years." },
          { kind: "para", text: "Examples: “I always ruin things.” “I am the strong one.” “People leave.” “I cannot trust anyone.” “I should be farther along.”" },
          { kind: "subhead", text: "The Evidence" },
          { kind: "para", text: "Write five things the old story fails to explain." },
          { kind: "para", text: "Look for exceptions. Look for context. Look for moments of courage, tenderness, refusal, humor, survival, loyalty or choice." },
          { kind: "subhead", text: "The Fuller Story" },
          { kind: "para", text: "Write the sentence again." },
          { kind: "para", text: "Do not make it more positive." },
          { kind: "para", text: "Make it more true." },
          { kind: "rule" },
          { kind: "subhead", text: "On Reflection" },
          {
            kind: "list",
            items: [
              "What story about yourself have you heard so often that you rarely question it?",
              "Who first told you that story?",
              "What did believing it help you survive?",
              "What evidence does the story leave out?",
              "When have you behaved in a way that contradicts it?",
              "If you were allowed to rewrite one sentence about yourself, what would it say?",
            ],
          },
        ],
      },
      {
        n: 6,
        label: "A Quiet Question",
        tone: "cover",
        hero: { src: closing, alt: "A lone figure walking a dune path toward moonlit water", width: 1280, height: 1600 },
        blocks: [
          { kind: "kicker", text: "A Quiet Question" },
          { kind: "heading", text: "What if the story isn’t finished?" },
          { kind: "subhead", text: "Begin" },
          { kind: "para", text: "Therapy does not require you to arrive with the right language." },
          { kind: "para", text: "You can begin with a sentence. A pattern. A question. A story you are no longer certain you believe." },
          { kind: "para", text: "The work can begin there." },
          { kind: "colophon", lines: colophon },
        ],
      },
    ],
  },
  {
    id: "healing-power-of-journaling",
    issue: "Issue Two",
    title: "The Healing Power of Journaling",
    standfirst: "Writing, mindfulness & the practice of hearing yourself",
    date: "September 2026",
    cover: { src: coverTwo, alt: "A woman seated in dune grass at dawn with a notebook in her lap", width: 1280, height: 1600 },
    pages: [
      {
        n: 1,
        label: "Cover",
        tone: "cover",
        hero: { src: coverTwo, alt: "A woman seated in dune grass at dawn with a notebook in her lap", width: 1280, height: 1600 },
        blocks: [
          { kind: "kicker", text: "The Inkwell Papers · Issue Two · September 2026" },
          { kind: "heading", text: "The Healing Power of Journaling" },
          { kind: "standfirst", text: "Writing, mindfulness & the practice of hearing yourself" },
          { kind: "colophon", lines: ["A journal from The Inkwell", "A Marriage & Family Therapy Practice"] },
        ],
      },
      {
        n: 2,
        label: "From The Inkwell",
        tone: "dark",
        blocks: [
          { kind: "kicker", text: "From The Inkwell" },
          { kind: "para", text: "There are thoughts we repeat privately for years without ever hearing ourselves say them." },
          { kind: "para", text: "Writing changes that." },
          { kind: "para", text: "A thought placed on paper becomes something we can look at. Sometimes we agree with it. Sometimes we are startled by it. Sometimes we recognize that the sentence we have been carrying does not belong to us at all." },
          { kind: "para", text: "A journal does not require eloquence. You do not need beautiful handwriting. You do not need a special notebook. You do not even need to know what you are trying to say." },
          { kind: "para", text: "You need only enough quiet to begin." },
          { kind: "para", text: "This issue of The Inkwell Papers is about writing as witness: a place where memory, feeling, contradiction, fear, tenderness, anger, grief, humor and possibility can occupy the same page." },
          { kind: "signoff", text: "— April" },
          {
            kind: "quote",
            text: "Give yourself permission to know the truth, and the truth will bubble up to the surface.",
            attribution: "Iyanla Vanzant",
            note: "Editorial source note: Iyanla Vanzant, Trust Life workbook, published through Iyanla.com.",
          },
          { kind: "image", src: portraitTwo, alt: "A man seated at the end of a dock at dusk, a closed notebook beside him", width: 1200, height: 1500 },
          { kind: "rule" },
          { kind: "heading", text: "The Healing Power of Journaling" },
          { kind: "standfirst", text: "Why writing can help us hear what we have been thinking all along." },
          { kind: "para", text: "You can think the same thought a thousand times and still be surprised when you see it written down. Inside the mind, thought moves quickly. It circles, edits, argues, predicts and repeats. On the page, a sentence has to slow down long enough to become visible." },
          { kind: "para", text: "That change in speed matters. Writing creates a small amount of distance between the person and the thought—not enough to make the thought unimportant, but enough to look at it. A sentence that felt absolute in the mind may look different in ink. “Nobody listens to me.” “I always disappoint people.” “I do not know what I want.” Once written, the sentence can be questioned." },
          { kind: "para", text: "Journaling is sometimes described as emotional release, and it can be. But release is only one function. Writing can also reveal repetition. The same name keeps appearing. The same conflict shows up in different relationships. The same word—trapped, invisible, responsible, tired—arrives on page after page." },
          { kind: "pull", text: "Repetition is information." },
          { kind: "para", text: "Repetition is information. It may point toward a need, a fear, a loyalty, a grief or a boundary that has not yet found language. A journal becomes a record of what the mind returns to when no one else is listening." },
          { kind: "para", text: "Writing also makes contradiction easier to tolerate. “I love them” and “I want to leave” can occupy the same page. “I am grateful” and “I am angry” do not have to fight for the right to exist. Therapy often requires exactly this kind of complexity: the ability to hold more than one truth without forcing an immediate verdict." },
          { kind: "para", text: "There is also agency in choosing what receives attention. A journal is one of the few places where the writer decides what gets to be important. No one interrupts. No algorithm selects the subject. No family member corrects the memory. The page can hold the thought long enough for the writer to discover what it is trying to say." },
          { kind: "para", text: "Journaling can become useful material in therapy, not because every entry needs to be shared, but because patterns become easier to notice. “I realized I only write about work using the word trapped.” “I noticed that I apologize to people even in my private journal.” “I wrote the same sentence three times.” Those observations are openings." },
          { kind: "para", text: "You do not have to write well. You only have to become willing to hear yourself." },
        ],
      },
      {
        n: 3,
        label: "Mindfulness on the Page",
        tone: "dark",
        hero: { src: fog, alt: "Fog lifting over still water at dawn with a distant figure", width: 1400, height: 1000 },
        blocks: [
          { kind: "heading", text: "Mindfulness on the Page" },
          { kind: "standfirst", text: "Writing without immediately judging what appears." },
          {
            kind: "quote",
            text: "One isn’t born with courage. One develops it.",
            attribution: "Maya Angelou",
            note: "Editorial source note: Maya Angelou, interview with Harvard Business Review, 2013.",
          },
          { kind: "para", text: "Most thoughts are evaluated almost as quickly as they appear. Good. Bad. Petty. Embarrassing. Selfish. Unreasonable. Dangerous. The judgment often arrives so quickly that we mistake it for the thought itself." },
          { kind: "para", text: "Mindfulness asks for a different first move: notice. Before deciding whether an emotion is justified, notice that it is present. Before solving a thought, notice its shape. Before explaining the body, notice what the body is doing." },
          { kind: "para", text: "Journaling can create the same pause. Instead of writing, “I shouldn’t be angry,” try, “I notice anger when I think about Sunday.”" },
          { kind: "para", text: "Instead of, “This is stupid,” write, “I notice that I want to dismiss this.” The second sentence is not more dramatic. It is more precise. It allows the judgment itself to become observable." },
          { kind: "para", text: "Precision matters because observation and interpretation are not the same thing. “My chest tightened when she called” is an observation. “She has power over me” is an interpretation. Both may be meaningful, but separating them creates room for curiosity." },
          { kind: "pull", text: "Attention is a form of care." },
          { kind: "para", text: "The body can become part of the page. Where do you feel the thought? Does your jaw tighten? Does your breathing change? Do your shoulders rise when you write about work? Do you become sleepy when you approach a certain memory? The point is not to diagnose the sensation. The point is to notice the whole experience." },
          { kind: "para", text: "A useful practice is the five-minute page. Set a timer. Write continuously. Do not correct grammar. Do not cross anything out. Do not reread immediately. Let the page receive what arrives without requiring it to become insight." },
          { kind: "para", text: "This is where mindfulness and courage quietly meet. Courage is not always confrontation. Sometimes it is the decision to stay present for a thought long enough to hear it without immediately defending against it." },
          { kind: "para", text: "Mindfulness is not passivity. Observation can lead to action, boundaries, conversation, repair or departure. It simply slows the rush toward conclusion long enough for the next step to be chosen rather than reflexive." },
          { kind: "para", text: "Attention is a form of care." },
        ],
      },
      {
        n: 4,
        label: "The Quiet Between",
        tone: "dark",
        blocks: [
          { kind: "kicker", text: "Photographic essay" },
          { kind: "heading", text: "The Quiet Between" },
          { kind: "standfirst", text: "A photographic essay about pauses: the moment before the sentence, the space after the argument, the walk before a decision." },
          { kind: "image", src: fog, alt: "Fog over still water", width: 1400, height: 1000, frame: true },
          { kind: "image", src: portraitTwo, alt: "A man alone on a dock at dusk", width: 1200, height: 1500, frame: true },
          { kind: "image", src: essay, alt: "Shoreline and dune grass after people have left", width: 1400, height: 1000, frame: true },
          { kind: "words", items: ["Pause.", "Notice.", "Return."] },
        ],
      },
      {
        n: 5,
        label: "Ten Minutes",
        tone: "ivory",
        blocks: [
          { kind: "heading", text: "Ten Minutes" },
          { kind: "standfirst", text: "A short mindfulness + writing practice." },
          {
            kind: "steps",
            items: [
              { label: "Minute 1", text: "Sit. Do not write yet." },
              { label: "Minutes 2–3", text: "Notice your breathing and your body." },
              { label: "Minutes 4–8", text: "Write without stopping. Begin with: “Today I am carrying…”" },
              { label: "Minute 9", text: "Underline one sentence." },
              { label: "Minute 10", text: "Write: “What does this sentence want me to know?” Then close the journal. Do not solve anything." },
            ],
          },
          { kind: "rule" },
          { kind: "subhead", text: "Four Questions for a Difficult Day" },
          { kind: "list", items: ["What happened?", "What did I feel?", "What did I need?", "What do I want to do next?"] },
          { kind: "para", text: "Those are four different questions. Give each one its own answer." },
          { kind: "rule" },
          { kind: "subhead", text: "What I Know / What I Fear" },
          { kind: "para", text: "Divide a page in two." },
          { kind: "para", text: "WHAT I KNOW: facts, things you can verify, things that have actually happened." },
          { kind: "para", text: "WHAT I FEAR: predictions, assumptions, possibilities, things your mind is trying to protect you from." },
          { kind: "para", text: "Notice how often fear introduces itself as certainty." },
        ],
      },
      {
        n: 6,
        label: "A Quiet Question",
        tone: "cover",
        hero: { src: closing, alt: "A lone figure walking a dune path toward moonlit water", width: 1280, height: 1600 },
        blocks: [
          { kind: "kicker", text: "A Quiet Question" },
          { kind: "heading", text: "What have you been trying not to know?" },
          { kind: "subhead", text: "Begin" },
          { kind: "para", text: "You do not need a perfect explanation before therapy begins." },
          { kind: "para", text: "Sometimes the first useful thing is simply noticing the sentence you keep returning to." },
          { kind: "para", text: "Bring the question. Bring the page. Bring what you have noticed." },
          { kind: "para", text: "We can begin there." },
          { kind: "colophon", lines: colophon },
        ],
      },
    ],
  },
];

export function getPaper(id: string) {
  return papers.find((p) => p.id === id);
}
